import { useEffect, useRef } from 'react';

import { eyePaths } from '../render/eyes';
import type { InnerEyeStyle, OuterEyeStyle } from '../domain/types';

export interface EyePreviewProps {
  readonly outerStyle: OuterEyeStyle;
  readonly innerStyle: InnerEyeStyle;
  readonly color: string;
  readonly background: string;
  readonly outerRadius: number;
  readonly innerRadius: number;
  readonly size?: number;
}

/**
 * Live preview of a single finder pattern.
 *
 * Holds the canvas by ref rather than looking it up with
 * `document.getElementById`. The old hook resolved the element by id and had
 * no dependency on mounting, so toggling the section closed and open again
 * produced a fresh, blank canvas that never got drawn into.
 */
export function EyePreview({
  outerStyle,
  innerStyle,
  color,
  background,
  outerRadius,
  innerRadius,
  size = 84,
}: EyePreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;

    const ratio = Math.max(1, window.devicePixelRatio);
    canvas.width = Math.round(size * ratio);
    canvas.height = Math.round(size * ratio);

    const ctx = canvas.getContext('2d');
    if (ctx === null) return;

    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, size, size);

    const { ring, pupil } = eyePaths(outerStyle, innerStyle, 0, 0, size, outerRadius, innerRadius);

    ctx.fillStyle = color;
    ctx.fill(new Path2D(ring));
    ctx.fill(new Path2D(pupil));
  }, [outerStyle, innerStyle, color, background, outerRadius, innerRadius, size]);

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label="Vorschau des Eckelements"
      className="rounded border"
      style={{ width: size, height: size }}
    />
  );
}
